import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Alert, Typography } from '@mui/material';
import { getTrialUsage } from '../../utils/api/trialUsage_apis';

export function TrialCreditsBanner() {
  const user = useSelector((state) => state.auth?.user);
  const [usage, setUsage] = useState(null);

  const isTrial = user?.plan_name === 'trial';

  const fetchUsage = useCallback(() => {
    if (!isTrial) return;
    getTrialUsage()
      .then((res) => {
        if (res.status === 'success' && res.data) {
          setUsage(res.data);
        }
      })
      .catch(() => setUsage(null));
  }, [isTrial]);

  useEffect(() => {
    if (!isTrial) return;
    let mounted = true;
    getTrialUsage()
      .then((res) => {
        if (mounted && res.status === 'success' && res.data) {
          setUsage(res.data);
        }
      })
      .catch(() => {
        if (mounted) setUsage(null);
      });
    return () => { mounted = false; };
  }, [isTrial]);

  useEffect(() => {
    const onRefresh = () => fetchUsage();
    window.addEventListener('trialUsageRefresh', onRefresh);
    return () => window.removeEventListener('trialUsageRefresh', onRefresh);
  }, [fetchUsage]);

  if (!isTrial) return null;

  const hasCreditLimit = usage && Number(usage.credit_limit) > 0;
  if (!usage || !hasCreditLimit) return null;

  const isExpired = usage.is_expired;
  const creditsRemaining = Number(usage.credits_remaining) ?? 0;
  const creditsUsedPercentRaw = Number(usage.credits_used_percent) ?? 0;
  /** API can exceed 100%; cap display only. Branches below still use the raw value. */
  const creditsUsedPercentDisplay = Number.isFinite(creditsUsedPercentRaw)
    ? Math.min(100, Math.max(0, creditsUsedPercentRaw))
    : 0;
  const fmt = (n) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');

  const text = `Credits remaining: ${fmt(creditsRemaining)}`;
  const pctText = creditsUsedPercentDisplay.toFixed(2);

  if (isExpired) {
    return (
      <Box sx={{ px: 1, py: 0.5 }}>
        <Alert severity="warning" variant="filled">
          <Typography variant="body2">Your trial has ended. Please upgrade your plan to continue.</Typography>
        </Alert>
      </Box>
    );
  }

  if (creditsUsedPercentRaw >= 100) {
    return (
      <Box sx={{ px: 1, py: 0.5 }}>
        <Alert severity="info" variant="filled">
          <Typography variant="body2">Credits fully used ({pctText}%). Please upgrade your plan to continue.</Typography>
        </Alert>
      </Box>
    );
  }

  if (creditsUsedPercentRaw >= 80) {
    return (
      <Box sx={{ px: 1, py: 0.5 }}>
        <Alert severity="warning">
          <Typography variant="body2">Credits running low ({pctText}% used). {text}</Typography>
        </Alert>
      </Box>
    );
  }

  // For usage below 80%, suppress the inline banner; the remaining-credit
  // summary is rendered in the sidebar instead.
  return null;
}

export default TrialCreditsBanner;
